import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Editor, EditorState, CompositeDecorator, convertFromRaw, convertToRaw, getDefaultKeyBinding, RichUtils, ContentState, Modifier } from 'draft-js';
import { isSaveAction, createRawFromText, getSelectionCollapseInfo, isEmptyContent } from 'lib/helpers';
import { linkDecorator } from '../../linkPlugin/linkDecorator';
import { inlineStyleMap } from '../../inlineStylePlugin/inlineStyleMap';
import { DraftViewer } from 'lib';

export class NestedEditor extends Component {
	static propTypes = {
		raw: PropTypes.oneOfType([
			PropTypes.object,
			PropTypes.string,
		]).isRequired
	}

	decorator = new CompositeDecorator([linkDecorator]);

	state = {
		editorState: this.getInitialEditorState(),
	};

	getStore() {
		return {
			getEditorEnviorment: () => 'table',
			getEditorState: () => this.state.editorState,
			setEditorState: this.handleChange,
		}
	}

	render() {
		const { focused, raw, onFocus, onBlur } = this.props;
		const cancelBubble = evt => evt.stopPropagation();
		return (
			<div
				className="table-nested-editor"
				ref={nested => this.element = nested}
				onSelect={cancelBubble}
				onKeyDown={cancelBubble}
				onChange={cancelBubble}>
				<Editor
					ref={editor => this.editor = editor}
					readOnly={!focused}
					editorState={this.state.editorState}
					onChange={this.handleChange}
					onBlur={onBlur}
					customStyleMap={inlineStyleMap}
					keyBindingFn={this.keyBindingFn}
					handlePastedText={this.handlePastedText}
					onUpArrow={this.keyBindingFn}
					onDownArrow={this.keyBindingFn}
					onLeftArrow={this.keyBindingFn}
					onRightArrow={this.keyBindingFn}
					handleKeyCommand={this.handleKeyCommand}
					onTab={this.keyBindingFn}
				/>
			</div>
		)
	}

	getInitialEditorState() {
		let { raw } = this.props;
		if (typeof raw === 'string') {
			raw = createRawFromText(raw);
		}
		const content = convertFromRaw(raw);
		return EditorState.createWithContent(content, this.decorator);
	}

	syncRaw() {
		this.setState({
			editorState: this.getInitialEditorState()
		});
	}

	clear = () => {
		this.setState({
			editorState: EditorState.createEmpty(this.decorator),
		});
	}

	handleChange = (/** @type {EditorState} */ editorState) => {
		const lastContent = this.state.editorState.getCurrentContent();
		const newContent = editorState.getCurrentContent();
		this.setState({ editorState }, () => {
			if (lastContent !== newContent && typeof this.props.onChange === 'function') {
				this.props.onChange(convertToRaw(newContent));
			} else {
				this.props.onSelectionChange();
			}
		})
	}

	handlePastedText = (text) => {
		const editorState = this.state.editorState;
		text = text.replace(/\t/g, '  ');
		text = text.replace(/\r\n/g, '\n');
		const selection = editorState.getSelection();
		const blockMap = ContentState.createFromText(text, /$/).getBlockMap();
		const newContent = Modifier.replaceWithFragment(editorState.getCurrentContent(), selection, blockMap);
		this.handleChange(EditorState.push(editorState, newContent, 'insert-fragment'));
		return 'handled';
	}

	keyBindingFn = (/** @type {React.KeyboardEvent}*/ evt) => {
		const { editorState } = this.state;
		const executeTableCommand = (command) => (
			evt.preventDefault(),
			this.props.onTableCommand(command),
			`table/${command}`
		);

		if (isSaveAction(evt) && typeof this.props.onSave === 'function') {
			evt.preventDefault();
			this.props.onSave(evt);
		}

		// TAB
		if (evt.keyCode === 9 && !evt.metaKey && !evt.ctrlKey && !evt.altKey) {
			return executeTableCommand(evt.shiftKey ? 'focus-prev' : 'focus-next');
		}
		// Enter
		if (evt.keyCode === 13) {
			if (evt.shiftKey && !evt.ctrlKey && !evt.metaKey && !evt.altKey) {
				// 支持软换行
				return 'softline';
			}
			// 想统一交互向下聚焦（对齐 Excel）
			executeTableCommand('focus-down');

			// 注释的部分对不同单元格区别看待
			// const { isAtFirstBlock, isAtLastBlock, isAtStartOfBlock, isAtEndOfBlock } = getSelectionCollapseInfo(editorState);
			// if (isAtFirstBlock && isAtStartOfBlock) {
			// 	executeTableCommand('return-start');
			// }
			// else if (isAtLastBlock && isAtEndOfBlock) {
			// 	executeTableCommand('return-end');
			// }
			// else {
			// 	executeTableCommand('focus-down');
			// }
			return null;
		}
		// Backspace
		if (evt.keyCode === 8) {
			if (isEmptyContent(editorState)) {
				return executeTableCommand('focus-prev');
			}
		}
		// Arrow Keys (LEFT = 37, UP = 38, RIGHT = 39, DOWN = 40)
		if ([37, 38, 39, 40].indexOf(evt.keyCode) > -1 && !evt.shiftKey && !evt.metaKey && !evt.ctrlKey && !evt.altKey) {
			const { isAtFirstBlock, isAtLastBlock, isAtStartOfBlock, isAtEndOfBlock } = getSelectionCollapseInfo(editorState);
			switch (evt.keyCode) {
				case 37: // LEFT
					if (isAtFirstBlock && isAtStartOfBlock) {
						this.props.onTableCommand('focus-left');
						evt.preventDefault();
						return;
					}
					break;
				case 38: // UP
					if (isAtFirstBlock && isAtStartOfBlock) {
						this.props.onTableCommand('focus-up');
						evt.preventDefault();
						return;
					}
					break;
				case 39: // RIGHT
					if (isAtLastBlock && isAtEndOfBlock) {
						this.props.onTableCommand('focus-right');
						evt.preventDefault();
						return;
					}
					break;
				case 40: // DOWN
					if (isAtLastBlock && isAtEndOfBlock) {
						this.props.onTableCommand('focus-down');
						evt.preventDefault();
						return;
					}
					break;
			}
		}
		return getDefaultKeyBinding(evt);
	}

	handleKeyCommand = command => {
		const { editorState } = this.state;

		if (command === 'softline') {
			this.handleChange(RichUtils.insertSoftNewline(editorState));
			return 'handled';
		}

		/** @type {EditorState} */
		let newState = RichUtils.handleKeyCommand(this.state.editorState, command);
		if (newState) {
			this.handleChange(newState);
			return 'handled';
		}
		return 'not-handled';
	};

	focus(shouldMoveCursorToEnd) {
		if (this.editor) {
			this.editor.focus();
			if (shouldMoveCursorToEnd) {
				this.handleChange(EditorState.moveFocusToEnd(this.state.editorState));
			}
		}
	}
}



// WEBPACK FOOTER //
// ./lib/editor/DraftEditor/plugins/tablePlugin/TableBlock/NestedEditor.jsx